/**
 * Admin Information Hub
 * Central page for managing news and handbook
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import {
  Info,
  Eye,
  Newspaper,
  BookOpen,
  Plus,
  Trash2,
  Pin,
  PinOff,
  Save,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Users,
  Clock,
  Calendar,
  FileText,
  Briefcase,
  Wrench,
  Settings,
  Palette,
  ChevronUp,
  ChevronDown
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { useAuth } from '../../lib/auth'
import {
  getNews,
  createNews,
  updateNews,
  deleteNews,
  getNewsReadStats
} from '../../services/news'
import {
  getHandbookSections,
  updateHandbookContent,
  getConfirmationStatistics,
  createHandbookSection,
  updateHandbookSection,
  deleteHandbookSection,
  getLatestHandbookVersion,
  getHandbookVersionHistory,
  publishNewHandbookVersion
} from '../../services/handbook'
import type { News, CreateNewsData } from '../../types/news'
import type { HandbookSectionWithContent } from '../../types/handbook'

type TabType = 'news' | 'handbook' | 'adminManual'

export function AdminTGInfo() {
  const [activeTab, setActiveTab] = useState<TabType>('news')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100 flex items-center space-x-3">
          <Info className="h-7 w-7" />
          <span>Information</span>
        </h1>
        <p className="text-nature-600 dark:text-nature-400">
          Hantera nyheter och handbok för trädgårdsmästare
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-nature-200 dark:border-nature-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('news')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'news'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-nature-500 hover:text-nature-700 hover:border-nature-300'
            }`}
          >
            <Newspaper className="h-4 w-4" />
            <span>Nyheter</span>
          </button>
          <button
            onClick={() => setActiveTab('handbook')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'handbook'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-nature-500 hover:text-nature-700 hover:border-nature-300'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            <span>Handbok</span>
          </button>
          <button
            onClick={() => setActiveTab('adminManual')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'adminManual'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-nature-500 hover:text-nature-700 hover:border-nature-300'
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>Admin Manual</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'news' && <NewsTab />}
      {activeTab === 'handbook' && <HandbookTab />}
      {activeTab === 'adminManual' && <AdminManualTab />}
    </div>
  )
}

// ============================================
// News Tab
// ============================================

type NewsRightPanelView = 'editor' | 'stats'

function NewsTab() {
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const [selectedNews, setSelectedNews] = useState<News | null>(null)
  const [rightPanelView, setRightPanelView] = useState<NewsRightPanelView>('editor')
  const [isCreating, setIsCreating] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [formData, setFormData] = useState<CreateNewsData>({
    title: '',
    content: '',
    summary: '',
    is_published: true,
    is_pinned: false
  })
  const [saving, setSaving] = useState(false)

  const { data: news = [], isLoading } = useQuery({
    queryKey: ['admin-news'],
    queryFn: () => getNews(true)
  })

  const { data: readStats } = useQuery({
    queryKey: ['news-read-stats', selectedNews?.id],
    queryFn: () => getNewsReadStats(selectedNews!.id),
    enabled: !!selectedNews && rightPanelView === 'stats'
  })

  const handleSelectNews = (newsItem: News) => {
    setSelectedNews(newsItem)
    setIsCreating(false)
    setRightPanelView('editor')
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      summary: newsItem.summary || '',
      is_published: newsItem.is_published,
      is_pinned: newsItem.is_pinned
    })
  }

  const handleNewNews = () => {
    setSelectedNews(null)
    setIsCreating(true)
    setRightPanelView('editor')
    setFormData({
      title: '',
      content: '',
      summary: '',
      is_published: true,
      is_pinned: false
    })
  }

  const handleShowStats = (newsItem: News) => {
    setSelectedNews(newsItem)
    setIsCreating(false)
    setRightPanelView('stats')
  }

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Fyll i titel och innehåll')
      return
    }

    setSaving(true)
    try {
      if (selectedNews && !isCreating) {
        await updateNews(selectedNews.id, formData)
        toast.success('Nyhet uppdaterad!')
      } else {
        const newNews = await createNews(formData, profile?.full_name || 'Admin')
        toast.success('Nyhet skapad!')
        setSelectedNews(newNews)
        setIsCreating(false)
      }
      queryClient.invalidateQueries({ queryKey: ['admin-news'] })
    } catch (error) {
      console.error('Error saving news:', error)
      toast.error('Kunde inte spara nyhet')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (newsItem: News) => {
    if (!confirm(`Vill du ta bort nyheten "${newsItem.title}"?`)) return

    try {
      await deleteNews(newsItem.id)
      queryClient.invalidateQueries({ queryKey: ['admin-news'] })
      if (selectedNews?.id === newsItem.id) {
        setSelectedNews(null)
        setIsCreating(false)
      }
      toast.success('Nyhet borttagen!')
    } catch (error) {
      console.error('Error deleting news:', error)
      toast.error('Kunde inte ta bort nyhet')
    }
  }

  const handleTogglePin = async (newsItem: News) => {
    try {
      await updateNews(newsItem.id, { is_pinned: !newsItem.is_pinned })
      queryClient.invalidateQueries({ queryKey: ['admin-news'] })
      toast.success(newsItem.is_pinned ? 'Nyhet avfäst' : 'Nyhet fäst')
    } catch (error) {
      console.error('Error toggling pin:', error)
      toast.error('Kunde inte uppdatera nyhet')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Panel - News List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-nature-900 dark:text-nature-100">Nyheter ({news.length})</h3>
          <button
            onClick={handleNewNews}
            className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            title="Ny nyhet"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
        {/* News Items */}
        {news.length === 0 ? (
          <div className="text-center py-8 text-nature-500">
            <Newspaper className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Inga nyheter ännu</p>
            <button
              onClick={handleNewNews}
              className="mt-2 text-primary-600 hover:underline text-sm"
            >
              Skapa första nyheten
            </button>
          </div>
        ) : (
          news.map((newsItem) => (
            <div
              key={newsItem.id}
              className={`p-4 rounded-lg border transition-colors ${
                selectedNews?.id === newsItem.id && rightPanelView === 'editor'
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                  : 'bg-[#faf9f7] dark:bg-nature-800 border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700'
              } ${!newsItem.is_published ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between">
                <button
                  onClick={() => handleSelectNews(newsItem)}
                  className="flex-1 text-left"
                >
                  <div className="flex items-center space-x-2">
                    {newsItem.is_pinned && (
                      <Pin className="h-3 w-3 text-amber-500" />
                    )}
                    <h4 className="font-medium text-nature-900 dark:text-nature-100 truncate">
                      {newsItem.title}
                    </h4>
                    {!newsItem.is_published && (
                      <span className="px-1.5 py-0.5 text-xs bg-nature-200 dark:bg-nature-600 text-nature-600 dark:text-nature-300 rounded">
                        Utkast
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-nature-500 mt-1">
                    {format(new Date(newsItem.published_at), 'yyyy-MM-dd')} • {newsItem.author_name}
                  </p>
                </button>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleShowStats(newsItem)}
                    className={`p-1 rounded transition-colors ${
                      selectedNews?.id === newsItem.id && rightPanelView === 'stats'
                        ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-nature-400 hover:text-nature-600 dark:hover:text-nature-300'
                    }`}
                    title="Visa statistik"
                  >
                    <Users className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleTogglePin(newsItem)}
                    className={`p-1 rounded transition-colors ${
                      newsItem.is_pinned
                        ? 'text-amber-600'
                        : 'text-nature-400 hover:text-nature-600 dark:hover:text-nature-300'
                    }`}
                    title={newsItem.is_pinned ? 'Avfäst' : 'Fäst'}
                  >
                    {newsItem.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(newsItem)}
                    className="p-1 text-nature-400 hover:text-red-500 rounded"
                    title="Ta bort"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Right Panel - Editor or Stats */}
      <div className="lg:col-span-2">
        {rightPanelView === 'editor' && (selectedNews || isCreating) ? (
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
            <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {isCreating ? 'Ny nyhet' : 'Redigera nyhet'}
              </h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`px-3 py-1.5 text-sm rounded-lg flex items-center space-x-2 ${
                    showPreview
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'bg-nature-100 text-nature-600 dark:bg-nature-700 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
                  }`}
                >
                  <Eye className="h-4 w-4" />
                  <span>{showPreview ? 'Redigera' : 'Förhandsgranska'}</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary flex items-center space-x-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span>{saving ? 'Sparar...' : 'Spara'}</span>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="form-label">Titel *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-field"
                  placeholder="Nyhetens titel..."
                />
              </div>
              <div>
                <label className="form-label">Sammanfattning</label>
                <input
                  type="text"
                  value={formData.summary || ''}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  className="input-field"
                  placeholder="Kort sammanfattning för listan..."
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="form-label mb-0">Innehåll * (Markdown)</label>
                </div>
                {showPreview ? (
                  <div className="min-h-[200px]">
                    <div className="mb-3 flex items-center space-x-2 text-sm text-nature-500">
                      <Eye className="h-4 w-4" />
                      <span>Förhandsgranskning - så här ser det ut för trädgårdsmästare</span>
                    </div>
                    <div className="prose prose-sm dark:prose-invert max-w-none p-4 bg-white dark:bg-nature-900 rounded-lg border border-nature-200 dark:border-nature-700">
                      <ReactMarkdown>{formData.content || '*Inget innehåll ännu*'}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="input-field font-mono text-sm"
                      rows={10}
                      placeholder="Skriv nyhetens innehåll..."
                    />
                    <div className="mt-4 p-4 bg-nature-50 dark:bg-nature-900 rounded-lg border border-nature-200 dark:border-nature-700">
                      <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3 text-sm">Formateringsguide</h4>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Rubriker</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`# Stor rubrik
## Mellanrubrik
### Liten rubrik`}
                          </code>
                        </div>
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Text</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`**Fetstil**
*Kursiv*
~~Genomstruken~~`}
                          </code>
                        </div>
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Listor</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`- Punkt 1
- Punkt 2
1. Numrerad
2. Lista`}
                          </code>
                        </div>
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Länkar</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`[Länktext](url)
> Citat`}
                          </code>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="rounded border-nature-300"
                  />
                  <span className="text-sm text-nature-700 dark:text-nature-300">Publicerad</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_pinned}
                    onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                    className="rounded border-nature-300"
                  />
                  <span className="text-sm text-nature-700 dark:text-nature-300">Fäst överst</span>
                </label>
              </div>
            </div>
          </div>
        ) : rightPanelView === 'stats' && selectedNews && readStats ? (
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Lässtatistik: {selectedNews.title}</span>
              </h3>
              <button
                onClick={() => handleSelectNews(selectedNews)}
                className="text-sm text-primary-600 hover:underline"
              >
                ← Tillbaka till redigering
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-nature-600 dark:text-nature-400">
                  {readStats.read_count} av {readStats.total_subcontractors} TG har läst
                </span>
                <span className="text-lg font-bold text-primary-600">
                  {readStats.read_percentage}%
                </span>
              </div>
              <div className="w-full bg-nature-200 dark:bg-nature-700 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{ width: `${readStats.read_percentage}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {readStats.readers.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-green-600 dark:text-green-400 mb-2 flex items-center space-x-1">
                    <CheckCircle className="h-4 w-4" />
                    <span>Har läst ({readStats.readers.length})</span>
                  </h4>
                  <ul className="space-y-1 max-h-60 overflow-y-auto">
                    {readStats.readers.map(r => (
                      <li key={r.subcontractor_id} className="text-sm text-nature-600 dark:text-nature-400 flex justify-between">
                        <span>{r.subcontractor_name}</span>
                        <span className="text-nature-400">{format(new Date(r.read_at), 'yyyy-MM-dd HH:mm')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {readStats.non_readers.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2 flex items-center space-x-1">
                    <XCircle className="h-4 w-4" />
                    <span>Har ej läst ({readStats.non_readers.length})</span>
                  </h4>
                  <ul className="space-y-1 max-h-60 overflow-y-auto">
                    {readStats.non_readers.map(r => (
                      <li key={r.subcontractor_id} className="text-sm text-nature-600 dark:text-nature-400">
                        {r.subcontractor_name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-12 text-center">
            <Newspaper className="h-12 w-12 text-nature-300 mx-auto mb-4" />
            <p className="text-nature-500 dark:text-nature-400">
              Välj en nyhet eller skapa en ny
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// Handbook Tab
// ============================================

// Available icons for sections
const AVAILABLE_ICONS = [
  { name: 'Calendar', icon: Calendar },
  { name: 'Clock', icon: Clock },
  { name: 'FileText', icon: FileText },
  { name: 'Briefcase', icon: Briefcase },
  { name: 'Wrench', icon: Wrench },
  { name: 'Info', icon: Info },
  { name: 'BookOpen', icon: BookOpen },
  { name: 'Users', icon: Users },
  { name: 'Settings', icon: Settings }
]

// Preset colors - expanded palette
const PRESET_COLORS = [
  // Row 1 - Blues & Cyans
  '#3B82F6', // blue
  '#2563EB', // blue-600
  '#1D4ED8', // blue-700
  '#06B6D4', // cyan
  '#0891B2', // cyan-600
  // Row 2 - Greens
  '#10B981', // emerald
  '#059669', // emerald-600
  '#84CC16', // lime
  '#22C55E', // green
  '#16A34A', // green-600
  // Row 3 - Yellows & Oranges
  '#F59E0B', // amber
  '#D97706', // amber-600
  '#F97316', // orange
  '#EA580C', // orange-600
  '#EAB308', // yellow
  // Row 4 - Reds & Pinks
  '#EF4444', // red
  '#DC2626', // red-600
  '#EC4899', // pink
  '#DB2777', // pink-600
  '#F43F5E', // rose
  // Row 5 - Purples & Grays
  '#8B5CF6', // violet
  '#7C3AED', // violet-600
  '#A855F7', // purple
  '#6B7280', // gray
  '#4B5563', // gray-600
]

type RightPanelView = 'section' | 'confirmations' | null

function HandbookTab() {
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const [selectedSection, setSelectedSection] = useState<HandbookSectionWithContent | null>(null)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [showSectionModal, setShowSectionModal] = useState(false)
  const [showVersionModal, setShowVersionModal] = useState(false)
  const [versionDescription, setVersionDescription] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [editingSection, setEditingSection] = useState<HandbookSectionWithContent | null>(null)
  const [rightPanelView, setRightPanelView] = useState<RightPanelView>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [sectionForm, setSectionForm] = useState({
    title: '',
    slug: '',
    description: '',
    icon: 'Info',
    color: '#6B7280',
    sort_order: 0
  })

  const { data: sections = [], isLoading: sectionsLoading } = useQuery({
    queryKey: ['admin-handbook-sections'],
    queryFn: getHandbookSections
  })

  const { data: confirmations = [], isLoading: confirmationsLoading } = useQuery({
    queryKey: ['handbook-confirmations'],
    queryFn: getConfirmationStatistics
  })

  const { data: currentVersion } = useQuery({
    queryKey: ['handbook-current-version'],
    queryFn: getLatestHandbookVersion
  })

  const { data: versionHistory = [] } = useQuery({
    queryKey: ['handbook-version-history'],
    queryFn: getHandbookVersionHistory
  })

  const handleSelectSection = (section: HandbookSectionWithContent) => {
    setSelectedSection(section)
    setEditContent(section.content?.content || '')
    setRightPanelView('section')
  }

  const handleShowConfirmations = () => {
    setRightPanelView('confirmations')
    setSelectedSection(null)
  }

  const handleSaveContent = async () => {
    if (!selectedSection) return

    setSaving(true)
    try {
      await updateHandbookContent(
        selectedSection.id,
        editContent,
        profile?.full_name || 'Admin'
      )
      queryClient.invalidateQueries({ queryKey: ['admin-handbook-sections'] })
      toast.success('Innehållet har sparats')
    } catch (error) {
      console.error('Error saving handbook:', error)
      toast.error('Kunde inte spara')
    } finally {
      setSaving(false)
    }
  }

  const handlePublishVersion = async () => {
    if (!versionDescription.trim()) {
      toast.error('Ange en beskrivning av ändringarna')
      return
    }

    setPublishing(true)
    try {
      await publishNewHandbookVersion(
        versionDescription,
        profile?.full_name || 'Admin'
      )
      queryClient.invalidateQueries({ queryKey: ['handbook-current-version'] })
      queryClient.invalidateQueries({ queryKey: ['handbook-version-history'] })
      queryClient.invalidateQueries({ queryKey: ['handbook-confirmations'] })
      toast.success('Ny version publicerad! Alla TG måste nu bekräfta handboken igen.')
      setShowVersionModal(false)
      setVersionDescription('')
    } catch (error) {
      console.error('Error publishing version:', error)
      toast.error('Kunde inte publicera ny version')
    } finally {
      setPublishing(false)
    }
  }

  const handleOpenSectionModal = (section?: HandbookSectionWithContent) => {
    if (section) {
      setEditingSection(section)
      setSectionForm({
        title: section.title,
        slug: section.slug,
        description: section.description || '',
        icon: section.icon || 'Info',
        color: section.color || '#6B7280',
        sort_order: section.sort_order
      })
    } else {
      setEditingSection(null)
      setSectionForm({
        title: '',
        slug: '',
        description: '',
        icon: 'Info',
        color: '#6B7280',
        sort_order: sections.length + 1
      })
    }
    setShowSectionModal(true)
  }

  const handleSaveSection = async () => {
    if (!sectionForm.title || !sectionForm.slug) {
      toast.error('Titel och slug krävs')
      return
    }

    setSaving(true)
    try {
      if (editingSection) {
        await updateHandbookSection(editingSection.id, {
          title: sectionForm.title,
          description: sectionForm.description || undefined,
          icon: sectionForm.icon,
          color: sectionForm.color,
          sort_order: sectionForm.sort_order
        })
        toast.success('Avsnitt uppdaterat')
      } else {
        await createHandbookSection({
          title: sectionForm.title,
          slug: sectionForm.slug.toLowerCase().replace(/[^a-z0-9]/g, '-'),
          description: sectionForm.description || undefined,
          icon: sectionForm.icon,
          color: sectionForm.color,
          sort_order: sectionForm.sort_order
        })
        toast.success('Avsnitt skapat')
      }
      queryClient.invalidateQueries({ queryKey: ['admin-handbook-sections'] })
      setShowSectionModal(false)
    } catch (error) {
      console.error('Error saving section:', error)
      toast.error('Kunde inte spara avsnitt')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteSection = async (section: HandbookSectionWithContent) => {
    if (!confirm(`Vill du verkligen ta bort avsnittet "${section.title}"? Detta tar även bort allt innehåll.`)) {
      return
    }

    try {
      await deleteHandbookSection(section.id)
      queryClient.invalidateQueries({ queryKey: ['admin-handbook-sections'] })
      if (selectedSection?.id === section.id) {
        setSelectedSection(null)
      }
      toast.success('Avsnitt borttaget')
    } catch (error) {
      console.error('Error deleting section:', error)
      toast.error('Kunde inte ta bort avsnitt')
    }
  }

  const handleMoveSection = async (section: HandbookSectionWithContent, direction: 'up' | 'down') => {
    const currentIndex = sections.findIndex(s => s.id === section.id)
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

    if (targetIndex < 0 || targetIndex >= sections.length) return

    const targetSection = sections[targetIndex]

    try {
      // Swap sort_order values
      await Promise.all([
        updateHandbookSection(section.id, { sort_order: targetSection.sort_order }),
        updateHandbookSection(targetSection.id, { sort_order: section.sort_order })
      ])
      queryClient.invalidateQueries({ queryKey: ['admin-handbook-sections'] })
    } catch (error) {
      console.error('Error moving section:', error)
      toast.error('Kunde inte flytta avsnitt')
    }
  }

  const getIconComponent = (iconName: string) => {
    const found = AVAILABLE_ICONS.find(i => i.name === iconName)
    return found ? found.icon : Info
  }

  const confirmedCount = confirmations.filter(c => !c.needs_reconfirmation).length
  const totalCount = confirmations.length

  if (sectionsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sections List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-nature-900 dark:text-nature-100">Avsnitt</h3>
            <button
              onClick={() => handleOpenSectionModal()}
              className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
              title="Lägg till avsnitt"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>

          {sections.map(section => {
            const IconComponent = getIconComponent(section.icon || 'Info')
            const sectionColor = section.color || '#6B7280'
            return (
              <div
                key={section.id}
                className={`p-4 rounded-lg border transition-colors ${
                  selectedSection?.id === section.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                    : 'bg-[#faf9f7] dark:bg-nature-800 border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <button
                    onClick={() => handleSelectSection(section)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center space-x-2">
                      <div
                        className="p-1.5 rounded"
                        style={{ backgroundColor: `${sectionColor}20`, color: sectionColor }}
                      >
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <h4 className="font-medium text-nature-900 dark:text-nature-100">{section.title}</h4>
                    </div>
                    {section.content ? (
                      <p className="text-xs text-nature-500 mt-1 ml-8">
                        Uppdaterad {format(new Date(section.content.updated_at), 'yyyy-MM-dd')}
                      </p>
                    ) : (
                      <p className="text-xs text-nature-400 mt-1 ml-8">Inget innehåll ännu</p>
                    )}
                  </button>
                  <div className="flex items-center space-x-1">
                    <div className="flex flex-col">
                      <button
                        onClick={() => handleMoveSection(section, 'up')}
                        disabled={sections.indexOf(section) === 0}
                        className="p-0.5 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Flytta upp"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleMoveSection(section, 'down')}
                        disabled={sections.indexOf(section) === sections.length - 1}
                        className="p-0.5 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Flytta ner"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleOpenSectionModal(section)}
                      className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
                      title="Redigera avsnitt"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSection(section)}
                      className="p-1 text-nature-400 hover:text-red-500"
                      title="Ta bort avsnitt"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}

          {sections.length === 0 && (
            <div className="text-center py-8 text-nature-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Inga avsnitt ännu</p>
              <button
                onClick={() => handleOpenSectionModal()}
                className="mt-2 text-primary-600 hover:underline text-sm"
              >
                Skapa första avsnittet
              </button>
            </div>
          )}

          {/* Handbook Status Section */}
          <div className="mt-8 pt-6 border-t border-nature-200 dark:border-nature-700">
            <h3 className="font-medium text-nature-900 dark:text-nature-100 mb-4">Handboksstatus</h3>
          </div>

          {/* Confirmation Stats - Clickable */}
          <button
            onClick={handleShowConfirmations}
            className={`w-full text-left p-4 rounded-lg border transition-colors ${
              rightPanelView === 'confirmations'
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                : 'bg-[#faf9f7] dark:bg-nature-800 border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-nature-900 dark:text-nature-100">Bekräftelser</h4>
              <span className="text-lg font-bold text-primary-600">
                {totalCount > 0 ? Math.round((confirmedCount / totalCount) * 100) : 0}%
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-nature-600 dark:text-nature-400">
                {confirmedCount} av {totalCount} TG har bekräftat
              </span>
            </div>
            <div className="w-full bg-nature-200 dark:bg-nature-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${confirmedCount === totalCount ? 'bg-green-500' : 'bg-primary-600'}`}
                style={{ width: `${totalCount > 0 ? (confirmedCount / totalCount) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-nature-500 mt-2">Klicka för att se detaljer →</p>
          </button>

          {/* Version Management */}
          <div className="mt-4 p-4 bg-[#faf9f7] dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700">
            <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3">Versionshantering</h4>

            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-nature-600 dark:text-nature-400">Publicerad version:</span>
              <span className="text-xl font-bold text-primary-600">
                {currentVersion?.version_number || 0}
              </span>
            </div>

            {currentVersion && (
              <div className="text-xs text-nature-500 mb-3 pb-3 border-b border-nature-200 dark:border-nature-700">
                <p className="italic">"{currentVersion.description}"</p>
                <p className="mt-1">
                  {format(new Date(currentVersion.published_at), 'yyyy-MM-dd HH:mm')}
                  {currentVersion.published_by_name && ` • ${currentVersion.published_by_name}`}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-nature-600 dark:text-nature-400">Nästa version:</span>
              <span className="text-xl font-bold text-green-600">
                {(currentVersion?.version_number || 0) + 1}
              </span>
            </div>

            <button
              onClick={() => setShowVersionModal(true)}
              className="w-full btn-primary text-sm"
            >
              Publicera version {(currentVersion?.version_number || 0) + 1}
            </button>
          </div>

          {/* Version History */}
          {versionHistory.length > 0 && (
            <div className="mt-4 p-4 bg-[#faf9f7] dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700">
              <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3">Alla versioner</h4>
              <ul className="space-y-2 max-h-40 overflow-y-auto text-sm">
                {versionHistory.map((v, index) => (
                  <li key={v.id} className={`${index === 0 ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-nature-600 dark:text-nature-400'}`}>
                    <span className="font-medium">v{v.version_number}</span>
                    {index === 0 && <span className="ml-1 text-xs bg-primary-100 dark:bg-primary-900/30 px-1.5 py-0.5 rounded">aktuell</span>}
                    <span className="mx-1">•</span>
                    <span className={index === 0 ? '' : 'text-nature-500'}>{v.description}</span>
                    <span className="text-xs text-nature-400 ml-1">
                      ({format(new Date(v.published_at), 'yyyy-MM-dd')})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="lg:col-span-2">
          {/* Confirmations Panel */}
          {rightPanelView === 'confirmations' && (
            <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
              <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-nature-900 dark:text-nature-100 flex items-center space-x-2">
                    <Users className="h-5 w-5" />
                    <span>Bekräftelser för version {currentVersion?.version_number || 0}</span>
                  </h3>
                  <span className="text-lg font-bold text-primary-600">
                    {confirmedCount}/{totalCount}
                  </span>
                </div>
                <div className="w-full bg-nature-200 dark:bg-nature-700 rounded-full h-2 mt-3">
                  <div
                    className={`h-2 rounded-full transition-all ${confirmedCount === totalCount ? 'bg-green-500' : 'bg-primary-600'}`}
                    style={{ width: `${totalCount > 0 ? (confirmedCount / totalCount) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="p-6">
                {confirmationsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-nature-400" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {confirmations.map(c => (
                      <div
                        key={c.subcontractor_id}
                        className={`p-3 rounded-lg border ${
                          c.needs_reconfirmation
                            ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                            : 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-nature-900 dark:text-nature-100">
                            {c.subcontractor_name}
                          </span>
                          {c.needs_reconfirmation ? (
                            <XCircle className="h-5 w-5 text-red-500" />
                          ) : (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                        <p className="text-xs text-nature-500 mt-1">
                          {c.needs_reconfirmation
                            ? c.confirmed_version
                              ? `Bekräftade v${c.confirmed_version}`
                              : 'Har aldrig bekräftat'
                            : c.confirmed_at
                              ? `Bekräftade ${format(new Date(c.confirmed_at), 'yyyy-MM-dd HH:mm')}`
                              : 'Bekräftad'
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Editor */}
          {rightPanelView === 'section' && selectedSection && (
            <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
              <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-nature-900 dark:text-nature-100">{selectedSection.title}</h3>
                  {selectedSection.content && (
                    <p className="text-xs text-nature-500">
                      Senast uppdaterad {format(new Date(selectedSection.content.updated_at), 'yyyy-MM-dd HH:mm')}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      showPreview
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400 hover:bg-nature-200 dark:hover:bg-nature-600'
                    }`}
                  >
                    <Eye className="h-4 w-4" />
                    <span>{showPreview ? 'Redigera' : 'Förhandsgranska'}</span>
                  </button>
                  <button
                    onClick={handleSaveContent}
                    disabled={saving}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    <span>{saving ? 'Sparar...' : 'Spara'}</span>
                  </button>
                </div>
              </div>
              <div className="p-6">
                {showPreview ? (
                  <div className="min-h-[400px]">
                    <div className="mb-3 flex items-center space-x-2 text-sm text-nature-500">
                      <Eye className="h-4 w-4" />
                      <span>Förhandsgranskning - så här ser det ut för trädgårdsmästare</span>
                    </div>
                    <div className="prose prose-sm dark:prose-invert max-w-none p-4 bg-white dark:bg-nature-900 rounded-lg border border-nature-200 dark:border-nature-700">
                      {editContent ? (
                        <ReactMarkdown>{editContent}</ReactMarkdown>
                      ) : (
                        <p className="text-nature-400 italic">Inget innehåll att visa...</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="input-field font-mono text-sm"
                      rows={18}
                      placeholder="Skriv handbokens innehåll här..."
                    />
                    <div className="mt-4 p-4 bg-nature-50 dark:bg-nature-900 rounded-lg border border-nature-200 dark:border-nature-700">
                      <h4 className="font-medium text-nature-900 dark:text-nature-100 mb-3 text-sm">Formateringsguide</h4>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Rubriker</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`# Stor rubrik
## Mellanrubrik
### Liten rubrik`}
                          </code>
                        </div>
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Text</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`**Fetstil**
*Kursiv*
~~Genomstruken~~`}
                          </code>
                        </div>
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Listor</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`- Punkt 1
- Punkt 2

1. Nummer 1
2. Nummer 2`}
                          </code>
                        </div>
                        <div>
                          <p className="font-medium text-nature-700 dark:text-nature-300 mb-1">Övrigt</p>
                          <code className="block bg-nature-200 dark:bg-nature-800 p-2 rounded text-nature-600 dark:text-nature-400 whitespace-pre">
{`[Länktext](url)

--- (linje)

> Citat`}
                          </code>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!rightPanelView && (
            <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-8 text-center">
              <BookOpen className="h-12 w-12 text-nature-300 mx-auto mb-4" />
              <p className="text-nature-500 dark:text-nature-400">Välj ett avsnitt för att redigera innehållet</p>
            </div>
          )}
        </div>
      </div>

      {/* Section Modal */}
      {showSectionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowSectionModal(false)} />
            <div className="relative bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  {editingSection ? 'Redigera avsnitt' : 'Nytt avsnitt'}
                </h3>
                <button
                  onClick={() => setShowSectionModal(false)}
                  className="text-nature-400 hover:text-nature-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Titel</label>
                  <input
                    type="text"
                    value={sectionForm.title}
                    onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                    className="input-field"
                    placeholder="T.ex. Schema"
                  />
                </div>

                {!editingSection && (
                  <div>
                    <label className="form-label">Slug (URL-namn)</label>
                    <input
                      type="text"
                      value={sectionForm.slug}
                      onChange={(e) => setSectionForm({ ...sectionForm, slug: e.target.value })}
                      className="input-field"
                      placeholder="t-ex-schema"
                    />
                    <p className="text-xs text-nature-500 mt-1">Används i URL:en, endast små bokstäver och bindestreck</p>
                  </div>
                )}

                <div>
                  <label className="form-label">Beskrivning (valfritt)</label>
                  <input
                    type="text"
                    value={sectionForm.description}
                    onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                    className="input-field"
                    placeholder="Kort beskrivning av avsnittet"
                  />
                </div>

                <div>
                  <label className="form-label">Ikon</label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_ICONS.map(({ name, icon: IconComp }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setSectionForm({ ...sectionForm, icon: name })}
                        className={`p-2 rounded-lg border transition-colors ${
                          sectionForm.icon === name
                            ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-600'
                            : 'border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700'
                        }`}
                        title={name}
                      >
                        <IconComp className="h-5 w-5" />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label flex items-center space-x-2">
                    <Palette className="h-4 w-4" />
                    <span>Färg</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setSectionForm({ ...sectionForm, color })}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          sectionForm.color === color
                            ? 'border-nature-900 dark:border-white scale-110'
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="form-label">Sorteringsordning</label>
                  <input
                    type="number"
                    value={sectionForm.sort_order}
                    onChange={(e) => setSectionForm({ ...sectionForm, sort_order: parseInt(e.target.value) || 0 })}
                    className="input-field w-24"
                    min="0"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSectionModal(false)}
                  className="btn-secondary"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSaveSection}
                  disabled={saving}
                  className="btn-primary flex items-center space-x-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  <span>{editingSection ? 'Uppdatera' : 'Skapa'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version Modal */}
      {showVersionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowVersionModal(false)} />
            <div className="relative bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Publicera ny version
                </h3>
                <button
                  onClick={() => setShowVersionModal(false)}
                  className="text-nature-400 hover:text-nature-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>OBS!</strong> När du publicerar en ny version måste alla TG bekräfta handboken igen.
                  </p>
                </div>

                <div>
                  <label className="form-label">Nuvarande version</label>
                  <p className="text-2xl font-bold text-primary-600">
                    {currentVersion?.version_number || 0}
                  </p>
                </div>

                <div>
                  <label className="form-label">Ny version</label>
                  <p className="text-2xl font-bold text-green-600">
                    {(currentVersion?.version_number || 0) + 1}
                  </p>
                </div>

                <div>
                  <label className="form-label">Beskriv ändringarna *</label>
                  <textarea
                    value={versionDescription}
                    onChange={(e) => setVersionDescription(e.target.value)}
                    className="input-field"
                    rows={4}
                    placeholder="T.ex. Uppdaterade rutiner för tidrapportering, nya riktlinjer för utrustning..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowVersionModal(false)
                    setVersionDescription('')
                  }}
                  className="btn-secondary"
                >
                  Avbryt
                </button>
                <button
                  onClick={handlePublishVersion}
                  disabled={publishing || !versionDescription.trim()}
                  className="btn-primary flex items-center space-x-2"
                >
                  {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
                  <span>{publishing ? 'Publicerar...' : 'Publicera'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ============================================
// Admin Manual Tab
// ============================================

interface AdminManualSection {
  id: string
  title: string
  icon: React.ElementType
  color: string
  content: string
}

const adminManualSections: AdminManualSection[] = [
  {
    id: 'overview',
    title: 'Översikt',
    icon: Info,
    color: '#3B82F6',
    content: `
## Välkommen till TSA Admin

TSA (Trädgård Support App) är ett komplett system för att hantera uppdrag, trädgårdsmästare, kunder och fakturering.

### Huvudfunktioner

- **Uppdragshantering** - Skapa, planera och följa upp uppdrag
- **Trädgårdsmästare** - Hantera TG:s profiler, prislistor och tillgänglighet
- **Kunder** - Kundregister med kontaktuppgifter och platser
- **Artiklar** - Tjänster och produkter med prissättning
- **Kalender** - Visuell planering av uppdrag
- **Rapporter** - Granska och godkänn arbetsrapporter
- **Fakturering** - Skapa fakturaunderlag

### Navigation

Använd sidomenyn för att navigera mellan de olika sektionerna. Klicka på sol/måne-ikonen för att växla mellan ljust och mörkt tema.
    `
  },
  {
    id: 'assignments',
    title: 'Uppdrag',
    icon: Calendar,
    color: '#8B5CF6',
    content: `
## Uppdragshantering

### Skapa nytt uppdrag

1. Gå till **Uppdrag** i menyn
2. Klicka på **"+ Nytt uppdrag"**
3. Fyll i:
   - **Titel** - kort beskrivning av uppdraget
   - **Kund** - välj från kundlistan
   - **Plats** - välj kundplats eller skapa ny
   - **Datum och tid** - när arbetet ska utföras
   - **Beskrivning** - detaljerad information
   - **Artiklar** - lägg till tjänster/produkter med planerat antal
4. Tilldela trädgårdsmästare
5. Klicka **Spara**

### Uppdragsstatus

| Status | Betydelse |
|--------|-----------|
| Utkast | Ej publicerat, kan redigeras fritt |
| Planerad | Schemalagt och synligt för TG |
| Pågående | Arbetet har påbörjats |
| Rapporterad | TG har skickat in rapport |
| Granskning | Rapport väntar på godkännande |
| Godkänd | Rapporten är godkänd |
| Fakturerad | Faktura har skickats |
| Slutförd | Uppdraget är helt klart |

### Granska rapporter

1. Gå till **Uppdrag** → filtrera på "Granskning"
2. Öppna uppdraget
3. Granska:
   - Rapporterade artiklar och antal
   - Bilder (före/efter)
   - Utlägg med kvitton
   - Anteckningar
4. Välj åtgärd:
   - **Godkänn** - rapporten är komplett
   - **Begär komplettering** - be TG komplettera

### Återkommande uppdrag

För regelbundna uppdrag (t.ex. gräsklippning varje vecka):

1. Skapa uppdraget som vanligt
2. Aktivera **"Återkommande"**
3. Välj intervall: dagligen, veckovis, varannan vecka, eller månadsvis
4. Ange slutdatum (valfritt)

Systemet skapar automatiskt nya uppdrag enligt schemat.
    `
  },
  {
    id: 'subcontractors',
    title: 'Trädgårdsmästare',
    icon: Users,
    color: '#10B981',
    content: `
## Hantera trädgårdsmästare

### Bjud in ny TG

1. Gå till **Trädgårdsmästare** i menyn
2. Klicka på **"+ Ny trädgårdsmästare"**
3. Fyll i:
   - Namn
   - E-post (används för inloggning)
   - Telefon
   - Färg (för kalendervisning)
4. Välj prislista (eller skapa senare)
5. Klicka **Bjud in**

TG:n får ett välkomstmail med länk för att skapa lösenord.

### Prislista

Varje TG kan ha en egen prislista som styr deras ersättning:

1. Gå till **Artiklar** → **Prislistor**
2. Skapa eller redigera en prislista
3. Tilldela prislistan till TG under deras profil

### Tillgänglighet

TG anger själva sin tillgänglighet i sin profil. Du kan se detta under:

**Trädgårdsmästare** → klicka på TG → **Tillgänglighet**

### Handboksbekräftelse

Under **Information** → **Handbok** kan du:
- Se vilka TG som bekräftat handboken
- Se vilka som behöver läsa om efter uppdatering
- Publicera ny version (kräver ombekräftelse)
    `
  },
  {
    id: 'customers',
    title: 'Kunder',
    icon: Users,
    color: '#F59E0B',
    content: `
## Kundhantering

### Skapa ny kund

1. Gå till **Kunder** i menyn
2. Klicka på **"+ Ny kund"**
3. Fyll i kunduppgifter:
   - Namn (person eller företag)
   - Kontaktperson
   - Telefon och e-post
   - Organisationsnummer (för företag)
4. Lägg till platser (arbetsadresser)

### Kundplatser

En kund kan ha flera platser (t.ex. olika fastigheter):

1. Öppna kunden
2. Klicka **"+ Lägg till plats"**
3. Fyll i:
   - Platsnamn (t.ex. "Sommarstugan")
   - Adress
   - Anteckningar (portkod, specialinfo etc.)

### Dölja kundinfo för TG

Om TG inte ska se kundens namn/telefon:

1. Öppna kunden
2. Aktivera **"Dölj kundinfo för TG"**

TG ser då bara adressen, inte kundens kontaktuppgifter.

### Kundprislista

Kunder kan ha egna prislistor (t.ex. rabatterade priser):

1. Gå till **Artiklar** → **Prislistor**
2. Skapa prislista av typ "Kund"
3. Tilldela till kunden under kundprofilen
    `
  },
  {
    id: 'articles',
    title: 'Artiklar',
    icon: FileText,
    color: '#EC4899',
    content: `
## Artiklar och priser

### Artikeltyper

| Typ | Användning |
|-----|------------|
| Tjänst | Utförda arbeten (t.ex. "Gräsklippning") |
| Produkt | Material (t.ex. "Blomjord 40L") |
| Timdebitering | Tidsbaserad debitering |

### Skapa artikel

1. Gå till **Artiklar** i menyn
2. Klicka **"+ Ny artikel"**
3. Fyll i:
   - Artikelnummer
   - Namn och beskrivning
   - Kategori
   - Typ (Tjänst/Produkt/Timdebitering)
   - Enhet (st, timme, m² etc.)
   - Momssats (25%, 12%, 6%, 0%)
   - RUT-klassificering (om tillämpligt)

### Prislistor

Prislistor styr priser för olika grupper:

**TG-prislistor** - Vad TG får betalt
**Kundprislistor** - Vad kunden betalar

1. Gå till **Artiklar** → **Prislistor**
2. Skapa ny eller redigera befintlig
3. Ange pris exkl. moms för varje artikel

### Kategorier

Organisera artiklar i kategorier:

1. Gå till **Artiklar** → **Kategorier**
2. Skapa kategorier med namn och färg
3. Tilldela artiklar till kategorier
    `
  },
  {
    id: 'calendar',
    title: 'Kalender & Planering',
    icon: Calendar,
    color: '#6366F1',
    content: `
## Kalender

### Kalendervy

Gå till **Planering** för att se alla uppdrag visuellt:

- **Månadsvy** - översikt över hela månaden
- **Veckofilter** - fokusera på en vecka i taget
- **TG-filter** - visa endast specifika TG

### Färgkodning

Varje TG har en egen färg som visas i kalendern. Ändra färg under TG:s profil.

### Drag & drop

Du kan flytta uppdrag genom att dra och släppa dem i kalendern (om funktionen är aktiverad).

### Röda dagar

Svenska helgdagar visas automatiskt i kalendern med röd markering.
    `
  },
  {
    id: 'invoicing',
    title: 'Fakturering',
    icon: FileText,
    color: '#14B8A6',
    content: `
## Fakturering

### Fakturaunderlag

När uppdrag är godkända kan du skapa fakturaunderlag:

1. Gå till **Uppdrag**
2. Filtrera på "Godkänd" eller "Redo för fakturering"
3. Markera uppdrag att fakturera
4. Klicka **"Exportera fakturaunderlag"**
5. Välj format (Excel)

### Innehåll i fakturaunderlag

- Kundinformation
- Uppdragsdetaljer
- Artiklar med antal och pris
- RUT-avdrag (om tillämpligt)
- Utlägg

### Markera som fakturerad

Efter att faktura skickats:

1. Öppna uppdraget
2. Ändra status till "Fakturerad"

Detta håller ordning på vilka uppdrag som är fakturerade.

### TG:s fakturaunderlag

TG kan själva exportera sitt fakturaunderlag under **Mina uppdrag** → **Fakturera**. Detta påverkar inte admin-flödet.
    `
  },
  {
    id: 'communication',
    title: 'Kommunikation',
    icon: Newspaper,
    color: '#EF4444',
    content: `
## SMS och kommunikation

### Skicka SMS

1. Gå till **SMS** i menyn
2. Välj mottagare (en eller flera TG)
3. Skriv meddelandet
4. Klicka **Skicka**

SMS skickas via 46elks och kostar enligt deras prislista.

### Automatiska SMS

Systemet skickar automatiska SMS vid:
- Nytt uppdrag tilldelat
- TG avsäger sig uppdrag
- Rapport inskickad (till admin)

### Nyheter

Publicera nyheter som visas på TG:s dashboard:

1. Gå till **Information** → **Nyheter**
2. Klicka **"+ Ny nyhet"**
3. Skriv titel och innehåll (markdown stöds)
4. Välj om den ska vara fäst överst
5. Publicera

### Kommentarer på uppdrag

Du kan kommunicera med TG via kommentarer direkt på uppdraget. TG ser kommentarerna när de öppnar uppdraget.
    `
  },
  {
    id: 'tips',
    title: 'Tips & tricks',
    icon: Briefcase,
    color: '#84CC16',
    content: `
## Tips för effektivt arbete

### Tangentbordsgenvägar

- **Ctrl + K** - Snabbsök (om aktiverat)
- **Esc** - Stäng dialogrutor

### Bulk-operationer

För att hantera flera uppdrag samtidigt:
1. Markera flera uppdrag i listan
2. Välj åtgärd i verktygsfältet

### Sök och filter

Använd sökfältet och filter för att snabbt hitta:
- Uppdrag (efter titel, kund, TG)
- Kunder (efter namn, telefon)
- TG (efter namn, e-post)

### Aktivitetslogg

Under **Aktivitet** kan du se alla händelser i systemet:
- Skapade/ändrade uppdrag
- Inskickade rapporter
- SMS som skickats
- Inloggningar

### Dark mode

Klicka på sol/måne-ikonen i sidomenyn för att växla mellan ljust och mörkt tema. Inställningen sparas automatiskt.
    `
  }
]

function AdminManualTab() {
  const [expandedSections, setExpandedSections] = useState<string[]>(['overview'])

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  const expandAll = () => {
    setExpandedSections(adminManualSections.map(s => s.id))
  }

  const collapseAll = () => {
    setExpandedSections([])
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
            Administratörsmanual
          </h2>
          <p className="text-sm text-nature-600 dark:text-nature-400">
            Lär dig använda TSA Admin - klicka på en sektion för att läsa mer
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={expandAll}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Expandera alla
          </button>
          <span className="text-nature-400">|</span>
          <button
            onClick={collapseAll}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Minimera alla
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {adminManualSections.map((section) => {
          const IconComponent = section.icon
          const isExpanded = expandedSections.includes(section.id)

          return (
            <div
              key={section.id}
              className="card overflow-hidden"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${section.color}20` }}
                  >
                    <IconComponent
                      className="h-5 w-5"
                      style={{ color: section.color }}
                    />
                  </div>
                  <span className="font-medium text-nature-900 dark:text-nature-100">
                    {section.title}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-nature-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-nature-400" />
                )}
              </button>

              {/* Section Content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-nature-100 dark:border-nature-700">
                  <div className="pt-4 prose prose-sm dark:prose-invert max-w-none prose-headings:text-nature-900 dark:prose-headings:text-nature-100 prose-p:text-nature-700 dark:prose-p:text-nature-300 prose-li:text-nature-700 dark:prose-li:text-nature-300 prose-strong:text-nature-900 dark:prose-strong:text-nature-100 prose-table:text-sm">
                    <ReactMarkdown>{section.content}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-nature-500 dark:text-nature-400 pt-4">
        <p>Version 1.27 • Senast uppdaterad 2026-01-23</p>
      </div>
    </div>
  )
}
